// Admin Panel Microsoft 365 Integration - Implementare completă
import React, { useState, useEffect } from 'react';
import {
  Mail,
  Calendar,
  Users,
  FileText,
  CheckCircle2,
  AlertCircle,
  Settings,
  Zap,
  Cloud,
  Shield,
  RefreshCw,
  Database,
  User,
  Clock,
  Activity,
  Video,
  MessageSquare,
  Phone,
  Globe,
  Link,
  Download,
  Upload,
  Folder,
  Search,
  Bell,
  Eye,
  Edit,
  Trash2,
  Plus,
  ExternalLink
} from 'lucide-react';

const Microsoft365Integration = () => {
  const [activeTab, setActiveTab] = useState('overview');
  const [isConnecting, setIsConnecting] = useState(false);
  const [lastSyncTime, setLastSyncTime] = useState('2025-01-15 16:30');

  // Mock connection status
  const [connectionStatus] = useState({
    outlook: 'connected',
    calendar: 'connected',
    teams: 'connected',
    sharepoint: 'connected',
    onedrive: 'connected',
    powerbi: 'warning'
  });

  // Mock statistics
  const [stats] = useState({
    emailsSynced: 2847,
    calendarEvents: 156,
    documentsShared: 89,
    teamsMeetings: 34,
    teamsMessages: 1205,
    oneDriveFiles: 567,
    lastSync: '2025-01-15 16:30',
    totalUsers: 12,
    activeUsers: 8,
    storageUsed: '45.7 GB'
  });

  // Mock services data
  const services = [
    {
      name: 'Outlook Email',
      icon: Mail,
      status: connectionStatus.outlook,
      description: 'Sincronizare email-uri și contacte',
      count: stats.emailsSynced,
      unit: 'email-uri',
      color: 'blue',
      features: ['Email sync', 'Contact sync', 'Calendar invites', 'Attachments']
    },
    {
      name: 'Calendar',
      icon: Calendar,
      status: connectionStatus.calendar,
      description: 'Evenimente și întâlniri sincronizate',
      count: stats.calendarEvents,
      unit: 'evenimente',
      color: 'green',
      features: ['Event sync', 'Meeting scheduling', 'Reminders', 'Room booking']
    },
    {
      name: 'Microsoft Teams',
      icon: Users,
      status: connectionStatus.teams,
      description: 'Chat-uri și meeting-uri Teams',
      count: stats.teamsMeetings,
      unit: 'meeting-uri',
      color: 'purple',
      features: ['Video calls', 'Chat messages', 'File sharing', 'Screen sharing']
    },
    {
      name: 'SharePoint',
      icon: Database,
      status: connectionStatus.sharepoint,
      description: 'Documente și biblioteci SharePoint',
      count: stats.documentsShared,
      unit: 'documente',
      color: 'orange',
      features: ['Document management', 'Version control', 'Workflows', 'Permissions']
    },
    {
      name: 'OneDrive',
      icon: Cloud,
      status: connectionStatus.onedrive,
      description: 'Stocare și sincronizare fișiere',
      count: stats.oneDriveFiles,
      unit: 'fișiere',
      color: 'cyan',
      features: ['File sync', 'Backup', 'Sharing', 'Version history']
    },
    {
      name: 'Power BI',
      icon: Activity,
      status: connectionStatus.powerbi,
      description: 'Rapoarte și dashboard-uri',
      count: 8,
      unit: 'rapoarte',
      color: 'yellow',
      features: ['Data visualization', 'Reports', 'Dashboards', 'Analytics']
    }
  ];

  // Mock recent activities
  const [recentActivities] = useState([
    {
      id: 1,
      type: 'email',
      icon: Mail,
      title: 'Email sincronizat de la client@exemplu.ro',
      time: 'Acum 5 minute',
      status: 'success'
    },
    {
      id: 2,
      type: 'calendar',
      icon: Calendar,
      title: 'Întâlnire programată: Măsurători Casa Cluj',
      time: 'Acum 15 minute',
      status: 'success'
    },
    {
      id: 3,
      type: 'teams',
      icon: Users,
      title: 'Teams meeting finalizat cu echipa instalatori',
      time: 'Acum 2 ore',
      status: 'success'
    },
    {
      id: 4,
      type: 'sharepoint',
      icon: FileText,
      title: 'Document actualizat: Contract_Template.docx',
      time: 'Acum 3 ore',
      status: 'success'
    },
    {
      id: 5,
      type: 'onedrive',
      icon: Cloud,
      title: 'Sincronizare OneDrive întârziată',
      time: 'Acum 4 ore',
      status: 'warning'
    }
  ]);

  const getStatusIcon = (status) => {
    switch (status) {
      case 'connected':
        return <CheckCircle2 className="w-5 h-5 text-green-600" />;
      case 'warning':
        return <AlertCircle className="w-5 h-5 text-yellow-600" />;
      case 'error':
        return <AlertCircle className="w-5 h-5 text-red-600" />;
      default:
        return <Clock className="w-5 h-5 text-gray-600" />;
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'connected': return 'text-green-600 bg-green-50 dark:bg-green-900/20';
      case 'warning': return 'text-yellow-600 bg-yellow-50 dark:bg-yellow-900/20';
      case 'error': return 'text-red-600 bg-red-50 dark:bg-red-900/20';
      default: return 'text-gray-600 bg-gray-50 dark:bg-gray-900/20';
    }
  };

  const getServiceColor = (color) => {
    const colors = {
      blue: 'text-blue-600 bg-blue-50 dark:bg-blue-900/20',
      green: 'text-green-600 bg-green-50 dark:bg-green-900/20',
      purple: 'text-purple-600 bg-purple-50 dark:bg-purple-900/20',
      orange: 'text-orange-600 bg-orange-50 dark:bg-orange-900/20',
      cyan: 'text-cyan-600 bg-cyan-50 dark:bg-cyan-900/20',
      yellow: 'text-yellow-600 bg-yellow-50 dark:bg-yellow-900/20'
    };
    return colors[color] || colors.blue;
  };

  const handleConnect = async (service) => {
    setIsConnecting(true);
    // Simulate connection process
    setTimeout(() => {
      setIsConnecting(false);
      setLastSyncTime(new Date().toLocaleString());
    }, 2000);
  };

  const handleSync = () => {
    setLastSyncTime(new Date().toLocaleString());
  };

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-900">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        
        {/* Header */}
        <div className="mb-8">
          <div className="flex items-center justify-between">
            <div className="flex items-center">
              <Mail className="w-8 h-8 text-blue-600 mr-3" />
              <div>
                <h1 className="text-3xl font-bold text-gray-900 dark:text-white">
                  Microsoft 365 Integration
                </h1>
              </div>
            </div>
            <div className="flex items-center space-x-3">
              <button 
                onClick={handleSync}
                className="px-4 py-2 border border-blue-300 text-blue-600 hover:bg-blue-50 dark:hover:bg-blue-900/20 font-medium rounded-lg transition-colors flex items-center"
              >
                <RefreshCw className="w-4 h-4 mr-2" />
                Sincronizează
              </button>
              <button className="px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white font-medium rounded-lg transition-colors flex items-center">
                <Settings className="w-4 h-4 mr-2" />
                Configurează
              </button>
            </div>
          </div>
        </div>

        {/* Connection Status Banner */}
        <div className="bg-gradient-to-r from-blue-50 to-cyan-50 dark:from-blue-900/20 dark:to-cyan-900/20 rounded-xl border border-blue-200 dark:border-blue-800 p-6 mb-8">
          <div className="flex items-center justify-between">
            <div className="flex items-center">
              <CheckCircle2 className="w-6 h-6 text-green-600 mr-3" />
              <div>
                <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
                  Conectat la Microsoft 365
                </h3>
                <p className="text-sm text-gray-600 dark:text-gray-400">
                  Tenant: botez-de-poveste.onmicrosoft.com • Ultima sincronizare: {stats.lastSync}
                </p>
              </div>
            </div>
            <button className="px-4 py-2 bg-white dark:bg-gray-800 hover:bg-gray-50 dark:hover:bg-gray-700 text-gray-700 dark:text-gray-300 border border-gray-300 dark:border-gray-600 font-medium rounded-lg transition-colors">
              Reconfigurează
            </button>
          </div>
        </div>

        {/* Stats Cards */}
        <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-4 mb-8">
          <div className="bg-white dark:bg-gray-800 p-4 rounded-xl shadow-soft border border-gray-200 dark:border-gray-700">
            <div className="flex items-center">
              <Mail className="w-8 h-8 text-blue-600 mr-3" />
              <div>
                <p className="text-2xl font-bold text-gray-900 dark:text-white">{stats.emailsSynced.toLocaleString()}</p>
                <p className="text-xs text-gray-500">Email-uri</p>
              </div>
            </div>
          </div>
          
          <div className="bg-white dark:bg-gray-800 p-4 rounded-xl shadow-soft border border-gray-200 dark:border-gray-700">
            <div className="flex items-center">
              <Calendar className="w-8 h-8 text-green-600 mr-3" />
              <div>
                <p className="text-2xl font-bold text-gray-900 dark:text-white">{stats.calendarEvents}</p>
                <p className="text-xs text-gray-500">Evenimente</p>
              </div>
            </div>
          </div>

          <div className="bg-white dark:bg-gray-800 p-4 rounded-xl shadow-soft border border-gray-200 dark:border-gray-700">
            <div className="flex items-center">
              <Users className="w-8 h-8 text-purple-600 mr-3" />
              <div>
                <p className="text-2xl font-bold text-gray-900 dark:text-white">{stats.teamsMeetings}</p>
                <p className="text-xs text-gray-500">Meetings</p>
              </div>
            </div>
          </div>

          <div className="bg-white dark:bg-gray-800 p-4 rounded-xl shadow-soft border border-gray-200 dark:border-gray-700">
            <div className="flex items-center">
              <FileText className="w-8 h-8 text-orange-600 mr-3" />
              <div>
                <p className="text-2xl font-bold text-gray-900 dark:text-white">{stats.documentsShared}</p>
                <p className="text-xs text-gray-500">Documente</p>
              </div>
            </div>
          </div>

          <div className="bg-white dark:bg-gray-800 p-4 rounded-xl shadow-soft border border-gray-200 dark:border-gray-700">
            <div className="flex items-center">
              <User className="w-8 h-8 text-indigo-600 mr-3" />
              <div>
                <p className="text-2xl font-bold text-gray-900 dark:text-white">{stats.activeUsers}/{stats.totalUsers}</p>
                <p className="text-xs text-gray-500">Utilizatori</p>
              </div>
            </div>
          </div>

          <div className="bg-white dark:bg-gray-800 p-4 rounded-xl shadow-soft border border-gray-200 dark:border-gray-700">
            <div className="flex items-center">
              <Cloud className="w-8 h-8 text-cyan-600 mr-3" />
              <div>
                <p className="text-2xl font-bold text-gray-900 dark:text-white">{stats.storageUsed}</p>
                <p className="text-xs text-gray-500">Stocare</p>
              </div>
            </div>
          </div>
        </div>

        {/* Tabs */}
        <div className="mb-8">
          <nav className="flex space-x-8">
            {[
              { id: 'overview', name: 'Prezentare Generală', icon: Activity },
              { id: 'services', name: 'Servicii', icon: Zap },
              { id: 'users', name: 'Utilizatori', icon: Users },
              { id: 'settings', name: 'Setări', icon: Settings }
            ].map((tab) => {
              const Icon = tab.icon;
              return (
                <button
                  key={tab.id}
                  onClick={() => setActiveTab(tab.id)}
                  className={`flex items-center px-3 py-2 rounded-lg font-medium transition-colors ${
                    activeTab === tab.id
                      ? 'bg-blue-100 text-blue-700 dark:bg-blue-900 dark:text-blue-300'
                      : 'text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200'
                  }`}
                >
                  <Icon className="w-4 h-4 mr-2" />
                  {tab.name}
                </button>
              );
            })}
          </nav>
        </div>

        {/* Tab Content */}
        {activeTab === 'overview' && (
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
            
            {/* Services Overview */}
            <div className="lg:col-span-2">
              <div className="bg-white dark:bg-gray-800 rounded-xl shadow-soft border border-gray-200 dark:border-gray-700 p-6">
                <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-6">
                  Status Servicii
                </h3>
                
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  {services.slice(0, 4).map((service, index) => {
                    const Icon = service.icon;
                    return (
                      <div key={index} className="border border-gray-200 dark:border-gray-600 rounded-lg p-4">
                        <div className="flex items-center justify-between mb-3">
                          <div className="flex items-center">
                            <div className={`p-2 rounded-lg ${getServiceColor(service.color)} mr-3`}>
                              <Icon className="w-5 h-5" />
                            </div>
                            <div>
                              <h4 className="font-medium text-gray-900 dark:text-white">{service.name}</h4>
                            </div>
                          </div>
                          {getStatusIcon(service.status)}
                        </div>

                        <p className="text-sm text-gray-600 dark:text-gray-400 mb-3">
                          {service.description}
                        </p>

                        <div className="flex items-center justify-between">
                          <div>
                            <p className="text-xl font-bold text-gray-900 dark:text-white">
                              {typeof service.count === 'number' ? service.count.toLocaleString() : service.count}
                            </p>
                            <p className="text-xs text-gray-500">{service.unit}</p>
                          </div>
                          <button className="text-xs text-blue-600 hover:text-blue-700 font-medium">
                            Vezi detalii
                          </button>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            </div>

            {/* Recent Activity */}
            <div>
              <div className="bg-white dark:bg-gray-800 rounded-xl shadow-soft border border-gray-200 dark:border-gray-700 p-6">
                <div className="flex items-center justify-between mb-4">
                  <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
                    Activitate Recentă
                  </h3>
                  <Activity className="w-5 h-5 text-gray-400" />
                </div>

                <div className="space-y-4">
                  {recentActivities.map((activity) => {
                    const Icon = activity.icon;
                    return (
                      <div key={activity.id} className="flex items-start p-3 bg-gray-50 dark:bg-gray-700 rounded-lg">
                        <div className={`p-1 rounded ${activity.status === 'success' ? 'bg-green-100 dark:bg-green-900' : 'bg-yellow-100 dark:bg-yellow-900'} mr-3 mt-0.5`}>
                          <Icon className={`w-4 h-4 ${activity.status === 'success' ? 'text-green-600' : 'text-yellow-600'}`} />
                        </div>
                        <div className="flex-1 min-w-0">
                          <p className="text-sm font-medium text-gray-900 dark:text-white">
                            {activity.title}
                          </p>
                          <p className="text-xs text-gray-500 dark:text-gray-400">{activity.time}</p>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            </div>
          </div>
        )}

        {activeTab === 'services' && (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {services.map((service, index) => {
              const Icon = service.icon;
              return (
                <div key={index} className="bg-white dark:bg-gray-800 rounded-xl shadow-soft border border-gray-200 dark:border-gray-700 p-6">
                  <div className="flex items-center justify-between mb-4">
                    <div className="flex items-center">
                      <div className={`p-3 rounded-xl ${getServiceColor(service.color)} mr-3`}>
                        <Icon className="w-6 h-6" />
                      </div>
                      <div>
                        <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
                          {service.name}
                        </h3>
                      </div>
                    </div>
                    {getStatusIcon(service.status)}
                  </div>

                  <p className="text-sm text-gray-600 dark:text-gray-400 mb-4">
                    {service.description}
                  </p>

                  <div className="flex items-center justify-between mb-4">
                    <div>
                      <p className="text-2xl font-bold text-gray-900 dark:text-white">
                        {typeof service.count === 'number' ? service.count.toLocaleString() : service.count}
                      </p>
                      <p className="text-xs text-gray-500">{service.unit}</p>
                    </div>
                  </div>

                  <div className="mb-4">
                    <h4 className="text-sm font-medium text-gray-900 dark:text-white mb-2">
                      Funcționalități:
                    </h4>
                    <div className="flex flex-wrap gap-1">
                      {service.features.map((feature, idx) => (
                        <span key={idx} className="px-2 py-1 bg-gray-100 dark:bg-gray-700 text-xs text-gray-600 dark:text-gray-300 rounded">
                          {feature}
                        </span>
                      ))}
                    </div>
                  </div>

                  <div className="flex space-x-2">
                    <button className="flex-1 px-3 py-2 text-sm bg-blue-600 hover:bg-blue-700 text-white rounded-lg transition-colors">
                      Configurează
                    </button>
                    <button className="px-3 py-2 text-sm border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700 rounded-lg transition-colors">
                      <ExternalLink className="w-4 h-4" />
                    </button>
                  </div>
                </div>
              );
            })}
          </div>
        )}

        {activeTab === 'users' && (
          <div className="bg-white dark:bg-gray-800 rounded-xl shadow-soft border border-gray-200 dark:border-gray-700 p-6">
            <div className="flex items-center justify-between mb-6">
              <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
                Utilizatori Microsoft 365
              </h3>
              <button className="px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white font-medium rounded-lg transition-colors flex items-center">
                <Plus className="w-4 h-4 mr-2" />
                Adaugă Utilizator
              </button>
            </div>

            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="border-b border-gray-200 dark:border-gray-700">
                    <th className="text-left py-3 px-4 font-medium text-gray-900 dark:text-white">Utilizator</th>
                    <th className="text-left py-3 px-4 font-medium text-gray-900 dark:text-white">Email</th>
                    <th className="text-left py-3 px-4 font-medium text-gray-900 dark:text-white">Rol</th>
                    <th className="text-left py-3 px-4 font-medium text-gray-900 dark:text-white">Status</th>
                    <th className="text-left py-3 px-4 font-medium text-gray-900 dark:text-white">Ultima activitate</th>
                    <th className="text-left py-3 px-4 font-medium text-gray-900 dark:text-white">Acțiuni</th>
                  </tr>
                </thead>
                <tbody>
                  {[
                    { name: 'Admin Sistem', email: 'admin@botez-de-poveste.ro', role: 'Administrator', status: 'Activ', lastActivity: '2025-01-15 16:20' },
                    { name: 'Maria Popescu', email: 'maria.popescu@botez-de-poveste.ro', role: 'Manager', status: 'Activ', lastActivity: '2025-01-15 15:45' },
                    { name: 'Ion Ionescu', email: 'ion.ionescu@botez-de-poveste.ro', role: 'Angajat', status: 'Inactiv', lastActivity: '2025-01-14 12:30' },
                    { name: 'Ana Marinescu', email: 'ana.marinescu@botez-de-poveste.ro', role: 'Angajat', status: 'Activ', lastActivity: '2025-01-15 14:10' }
                  ].map((user, index) => (
                    <tr key={index} className="border-b border-gray-100 dark:border-gray-800 hover:bg-gray-50 dark:hover:bg-gray-700">
                      <td className="py-3 px-4">
                        <div className="flex items-center">
                          <div className="w-8 h-8 bg-blue-100 dark:bg-blue-900 rounded-full flex items-center justify-center mr-3">
                            <User className="w-4 h-4 text-blue-600" />
                          </div>
                          <span className="font-medium text-gray-900 dark:text-white">{user.name}</span>
                        </div>
                      </td>
                      <td className="py-3 px-4 text-gray-600 dark:text-gray-400">{user.email}</td>
                      <td className="py-3 px-4">
                        <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                          user.role === 'Administrator' ? 'bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-300' :
                          user.role === 'Manager' ? 'bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-300' :
                          'bg-gray-100 text-gray-800 dark:bg-gray-900 dark:text-gray-300'
                        }`}>
                          {user.role}
                        </span>
                      </td>
                      <td className="py-3 px-4">
                        <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                          user.status === 'Activ' ? 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-300' :
                          'bg-gray-100 text-gray-800 dark:bg-gray-900 dark:text-gray-300'
                        }`}>
                          {user.status}
                        </span>
                      </td>
                      <td className="py-3 px-4 text-sm text-gray-600 dark:text-gray-400">{user.lastActivity}</td>
                      <td className="py-3 px-4">
                        <div className="flex space-x-2">
                          <button className="p-1 text-gray-400 hover:text-blue-600 rounded">
                            <Edit className="w-4 h-4" />
                          </button>
                          <button className="p-1 text-gray-400 hover:text-red-600 rounded">
                            <Trash2 className="w-4 h-4" />
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {activeTab === 'settings' && (
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
            
            {/* Connection Settings */}
            <div className="bg-white dark:bg-gray-800 rounded-xl shadow-soft border border-gray-200 dark:border-gray-700 p-6">
              <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-6">
                Setări Conexiune
              </h3>
              
              <div className="space-y-6">
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    Tenant ID
                  </label>
                  <input
                    type="text"
                    value="12345678-1234-1234-1234-123456789012"
                    readOnly
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-gray-50 dark:bg-gray-700 text-gray-900 dark:text-white"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    Application ID
                  </label>
                  <input
                    type="text"
                    value="87654321-4321-4321-4321-210987654321"
                    readOnly
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-gray-50 dark:bg-gray-700 text-gray-900 dark:text-white"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    Interval sincronizare (minute)
                  </label>
                  <select className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white">
                    <option value="5">5 minute</option>
                    <option value="15" selected>15 minute</option>
                    <option value="30">30 minute</option>
                    <option value="60">1 oră</option>
                  </select>
                </div>
              </div>
            </div>

            {/* Sync Settings */}
            <div className="bg-white dark:bg-gray-800 rounded-xl shadow-soft border border-gray-200 dark:border-gray-700 p-6">
              <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-6">
                Setări Sincronizare
              </h3>
              
              <div className="space-y-6">
                <div>
                  <label className="flex items-center">
                    <input
                      type="checkbox"
                      defaultChecked
                      className="w-4 h-4 text-blue-600 bg-gray-100 border-gray-300 rounded focus:ring-blue-500 dark:focus:ring-blue-600 dark:ring-offset-gray-800 focus:ring-2 dark:bg-gray-700 dark:border-gray-600"
                    />
                    <span className="ml-2 text-sm text-gray-900 dark:text-white">
                      Sincronizare automată email-uri
                    </span>
                  </label>
                </div>

                <div>
                  <label className="flex items-center">
                    <input
                      type="checkbox"
                      defaultChecked
                      className="w-4 h-4 text-blue-600 bg-gray-100 border-gray-300 rounded focus:ring-blue-500 dark:focus:ring-blue-600 dark:ring-offset-gray-800 focus:ring-2 dark:bg-gray-700 dark:border-gray-600"
                    />
                    <span className="ml-2 text-sm text-gray-900 dark:text-white">
                      Sincronizare evenimente calendar
                    </span>
                  </label>
                </div>

                <div>
                  <label className="flex items-center">
                    <input
                      type="checkbox"
                      defaultChecked
                      className="w-4 h-4 text-blue-600 bg-gray-100 border-gray-300 rounded focus:ring-blue-500 dark:focus:ring-blue-600 dark:ring-offset-gray-800 focus:ring-2 dark:bg-gray-700 dark:border-gray-600"
                    />
                    <span className="ml-2 text-sm text-gray-900 dark:text-white">
                      Sincronizare contacte
                    </span>
                  </label>
                </div>

                <div>
                  <label className="flex items-center">
                    <input
                      type="checkbox"
                      className="w-4 h-4 text-blue-600 bg-gray-100 border-gray-300 rounded focus:ring-blue-500 dark:focus:ring-blue-600 dark:ring-offset-gray-800 focus:ring-2 dark:bg-gray-700 dark:border-gray-600"
                    />
                    <span className="ml-2 text-sm text-gray-900 dark:text-white">
                      Sincronizare fișiere OneDrive
                    </span>
                  </label>
                </div>

                <div>
                  <label className="flex items-center">
                    <input
                      type="checkbox"
                      defaultChecked
                      className="w-4 h-4 text-blue-600 bg-gray-100 border-gray-300 rounded focus:ring-blue-500 dark:focus:ring-blue-600 dark:ring-offset-gray-800 focus:ring-2 dark:bg-gray-700 dark:border-gray-600"
                    />
                    <span className="ml-2 text-sm text-gray-900 dark:text-white">
                      Notificări pentru erori de sincronizare
                    </span>
                  </label>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default Microsoft365Integration;